import { useContext, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { UserContext } from "../../../../hooks/UserContext";
import { API } from "../../../../api";
import type { AgrianTypes, Branch, Deal, Job } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { Agrian } from "../../../../utilities/integrations";
import { FileOptionTypes, GeoJSONOption } from "../../../../utilities/types";
import type { FeatureStats } from "../../ImportTable/ImportTable/ImportTable.types";
import { ImportFormAgrianViewModel } from "./AgrianImportForm.types";
import {
	getOrgIdsFromBranchesAndCompany,
	isEventAssociatedWithField,
	getLabAccountNumber,
	optionName,
} from "./AgrianImportForm.helpers";
import { ImportFormProps } from "../../shared/ImportHelpers";
import { defaultJobForImport } from "../../../../models";
import { normalizePointsFC } from "../../../../domain/sample/sampleService";

export function useImportFormAgrianViewModel(props: ImportFormProps): ImportFormAgrianViewModel {
	// Context
	const userContext = useContext(UserContext);

	// State
	const [organizations, setOrganizations] = useState<AgrianTypes.Organization[]>([]);
	const [selectedOrganizations, setSelectedOrganizations] = useState<AgrianTypes.Organization[]>([]);
	const [growers, setGrowers] = useState<AgrianTypes.Grower[]>([]);
	const [selectedGrowers, setSelectedGrowers] = useState<AgrianTypes.Grower[]>([]);
	const [farms, setFarms] = useState<AgrianTypes.Farm[]>([]);
	const [fields, setFields] = useState<AgrianTypes.Field[]>([]);
	const [selectedFields, setSelectedFields] = useState<AgrianTypes.Field[]>([]);
	const [selectedLabPackages] = useState<AgrianTypes.LabPackage[]>([]);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [loading, setLoading] = useState<string | boolean>(false);

	// Internal Branch/Deal selection state
	const [internalSelectedBranch, setInternalSelectedBranch] = useState<Branch | null>(
		props.selectedBranches?.[0] ?? null,
	);
	const [internalSelectedDeal, setInternalSelectedDeal] = useState<Deal | null>(props.selectedDeals?.[0] ?? null);

	// Computed values
	const orgIds = useMemo(
		() =>
			getOrgIdsFromBranchesAndCompany(internalSelectedBranch ? [internalSelectedBranch] : [], props.selectedCompany),
		[internalSelectedBranch, props.selectedCompany],
	);

	const canShowBranchSelector = useMemo(() => !!props.branches && props.branches.length > 0, [props.branches]);

	const canLoadOrganizations = useMemo(
		() => !!internalSelectedDeal && (props.user?.isSuperUser || orgIds.length > 1),
		[internalSelectedDeal, props.user?.isSuperUser, orgIds.length],
	);

	const showOrganizationSelector = useMemo(
		() => organizations.length > 0 && (props.user?.isSuperUser || orgIds.length > 1),
		[organizations.length, props.user?.isSuperUser, orgIds.length],
	);

	const canLoadGrowers = useMemo(
		() => !!selectedOrganizations.length || orgIds.length === 1,
		[selectedOrganizations.length, orgIds.length],
	);

	const showGrowerSelector = useMemo(() => growers.length > 0, [growers.length]);

	const canLoadFarmsAndFields = useMemo(() => !!selectedGrowers.length, [selectedGrowers.length]);

	const showFieldSelector = useMemo(() => fields.length > 0, [fields.length]);

	const canLoadJobs = useMemo(() => !!selectedFields.length, [selectedFields.length]);

	const showLoadingSpinner = useMemo(() => loading === "submit", [loading]);

	const showImportTable = useMemo(() => jobs.length > 0 && !props.isInModal, [jobs.length, props.isInModal]);

	// Reset functions (internal)
	function resetSampleEvents() {
		setJobs([]);
		setFeatureStats(undefined);
	}

	function resetFields() {
		setFields([]);
		setSelectedFields([]);
		resetSampleEvents();
	}

	function resetFarms() {
		setFarms([]);
		resetFields();
	}

	function resetGrowers() {
		setGrowers([]);
		setSelectedGrowers([]);
		resetFarms();
	}

	function resetOrganizations() {
		setOrganizations([]);
		setSelectedOrganizations([]);
		resetGrowers();
	}

	// Change handlers
	const handleOrganizationChange = useCallback((_event: any, value: AgrianTypes.Organization[]) => {
		setSelectedOrganizations(value);
		resetGrowers();
	}, []);

	const handleGrowerChange = useCallback((_event: any, value: AgrianTypes.Grower[]) => {
		setSelectedGrowers(value);
		resetFarms();
	}, []);

	const handleFieldChange = useCallback((_event: any, value: AgrianTypes.Field[]) => {
		setSelectedFields(value);
	}, []);

	const resetForm = useCallback(() => {
		resetOrganizations();
	}, []);

	// Internal Branch/Deal handlers
	const handleInternalBranchChange = useCallback((branch: Branch | null) => {
		setInternalSelectedBranch(branch);
		setInternalSelectedDeal(null);
		resetOrganizations();
	}, []);

	const handleInternalDealChange = useCallback((deal: Deal | null) => {
		setInternalSelectedDeal(deal);
		resetOrganizations();
	}, []);

	// API calls
	const getOrganizations = useCallback(async () => {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		setLoading(true);
		resetOrganizations();
		const loadingOrganizationToast = toast.loading("Loading Organizations...");
		try {
			let loadedOrganizations: AgrianTypes.Organization[] = [];
			if (props.user?.isSuperUser) {
				loadedOrganizations = await API.Integrations.Agrian.getOrganizations(props.selectedCompany.agrianType);
			} else {
				const currentOrgIds = getOrgIdsFromBranchesAndCompany(
					internalSelectedBranch ? [internalSelectedBranch] : [],
					props.selectedCompany,
				);
				if (currentOrgIds.length > 1) {
					loadedOrganizations = await API.Integrations.Agrian.getOrganizationsByIds(
						currentOrgIds,
						props.selectedCompany.agrianType,
					);
				}
			}
			const uniqueOrganizations = Array.from(new Map(loadedOrganizations.map((o) => [o.id, o])).values());
			setOrganizations(uniqueOrganizations);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.AgrianImportForm.getOrganizations");
		} finally {
			setLoading(false);
			if (loadingOrganizationToast && toast.isActive(loadingOrganizationToast)) toast.dismiss(loadingOrganizationToast);
		}
	}, [props.selectedCompany, props.user?.isSuperUser, internalSelectedBranch]);

	const getGrowers = useCallback(async () => {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		setLoading(true);
		resetGrowers();
		const loadingGrowerToast = toast.loading("Loading Growers...");

		try {
			const loadOrgIds = selectedOrganizations.map((org) => org.id);
			if (selectedOrganizations.length === 0) {
				const currentOrgIds = getOrgIdsFromBranchesAndCompany(
					internalSelectedBranch ? [internalSelectedBranch] : [],
					props.selectedCompany,
				);
				for (const orgId of currentOrgIds) {
					if (!loadOrgIds.includes(orgId)) {
						loadOrgIds.push(orgId);
					}
				}
			}
			const loadedGrowers = await API.Integrations.Agrian.getGrowers(loadOrgIds, props.selectedCompany.agrianType);
			const uniqueGrowers = Array.from(new Map(loadedGrowers.map((g) => [g.id, g])).values());
			setGrowers(uniqueGrowers);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.AgrianImportForm.getGrowers");
		} finally {
			setLoading(false);
			if (loadingGrowerToast && toast.isActive(loadingGrowerToast)) toast.dismiss(loadingGrowerToast);
		}
	}, [props.selectedCompany, selectedOrganizations, internalSelectedBranch]);

	const getFarmsAndFields = useCallback(async () => {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		const loadingFarmsAndFieldsToast = toast.loading("Loading Farms and Fields...");
		setLoading(true);
		resetFarms();
		try {
			const growerIds = selectedGrowers.map((grower) => grower.id);
			const loadedFarms = await API.Integrations.Agrian.getFarms(growerIds, props.selectedCompany.agrianType);
			const uniqueFarms = Array.from(new Map(loadedFarms.map((f) => [f.id, f])).values());
			setFarms(uniqueFarms);

			const farmIds = uniqueFarms.map((farm) => farm.id);
			const loadedFields = await API.Integrations.Agrian.getFields(farmIds, props.selectedCompany.agrianType);
			const uniqueFields = Array.from(new Map(loadedFields.map((f) => [f.id, f])).values());
			setFields(uniqueFields);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.AgrianImportForm.getFarmsAndFields");
		} finally {
			setLoading(false);
			if (loadingFarmsAndFieldsToast && toast.isActive(loadingFarmsAndFieldsToast))
				toast.dismiss(loadingFarmsAndFieldsToast);
		}
	}, [props.selectedCompany, selectedGrowers]);

	const getSamplingEvents = useCallback(async () => {
		if (!(props.selectedCompany && props.selectedCompany.agrianType)) return;
		if (selectedLabPackages.length > 1) {
			toast.error("Please select only one lab test package.");
			return;
		}
		setLoading(true);
		resetSampleEvents();
		const loadingEvents = toast.loading("Loading...");

		if (!internalSelectedDeal) {
			toast.error("No deal selected");
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
			return;
		}
		const _growerDealMap: Record<string, Deal> = {};
		const _growerBranchMap: Record<string, Branch> = {};
		for (const grower of selectedGrowers) {
			_growerDealMap[grower.id] = internalSelectedDeal;
			if (internalSelectedBranch) {
				_growerBranchMap[grower.id] = internalSelectedBranch;
			}
		}

		try {
			setJobs([]);
			setFeatureStats(undefined);

			const isZoneDeal = internalSelectedDeal.type === "Zone";

			const fieldIds = selectedFields.map((field) => field.id);

			const total = fieldIds.length + 2;
			let loaded = 0;

			if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
			const targetEvents = await API.Integrations.Agrian.getTargetEvents(fieldIds, props.selectedCompany.agrianType);
			loaded++;

			if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
			const zones = isZoneDeal
				? await API.Integrations.Agrian.getZones(fieldIds, props.selectedCompany.agrianType)
				: [];
			loaded++;

			// The Sample Events endpoint is very slow, so we're calling it once per field
			// to show the user a progress bar, so they know something is happening.
			const sampleEvents: AgrianTypes.HydratedSampleEvent[] = [];
			for (let i = 0; i < fieldIds.length; i++) {
				const fieldId = fieldIds[i];
				if (loadingEvents) toast.update(loadingEvents, { progress: loaded / total });
				const fieldSampleEvents = await API.Integrations.Agrian.getSampleEvents(
					[fieldId],
					props.selectedCompany.agrianType,
				);
				sampleEvents.push(...fieldSampleEvents);
				loaded++;
			}

			const deal = internalSelectedDeal;

			const newJobs: Job[] = [];
			for (const field of selectedFields) {
				const farm = farms.find((f) => f.id === field.farm_id);
				if (!farm) continue;
				const grower = selectedGrowers.find((g) => g.id === farm?.grower_id);
				if (!grower) continue;

				const boundaryGeoJSON = Agrian.getBoundaryGeoJSON([field.boundary_map]);

				const fieldTargetEvents = targetEvents
					.filter((event) => isEventAssociatedWithField(event, field))
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const targetCentroidsGeoJSON: GeoJSON.FeatureCollection[] = [];
				const targetZonesGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldTargetEvents) {
					const centroidsGeoJSON = await Agrian.getTargetEventCentroidsGeoJSON(event);
					if (centroidsGeoJSON) targetCentroidsGeoJSON.push(centroidsGeoJSON);

					const zonesGeoJSON = await Agrian.getTargetEventZonesGeoJSON(event);
					if (zonesGeoJSON) targetZonesGeoJSON.push(zonesGeoJSON);
				}

				// Collect GeoJSON from sample events
				const fieldSampleEvents = sampleEvents
					.filter((event) => event.field_id === field.id)
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const sampleCentroidsGeoJSON: GeoJSON.FeatureCollection[] = [];
				const sampleCoresGeoJSON: GeoJSON.FeatureCollection[] = [];
				const sampleZonesGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldSampleEvents) {
					const centroidsGeoJSON = await Agrian.getSampleEventCentroidsGeoJSON(event);
					if (centroidsGeoJSON) sampleCentroidsGeoJSON.push(centroidsGeoJSON);

					const coresGeoJSON = await Agrian.getSampleEventCoresGeoJSON(event);
					if (coresGeoJSON) sampleCoresGeoJSON.push(coresGeoJSON);

					const zonesGeoJSON = await Agrian.getSampleEventZonesGeoJSON(event);
					if (zonesGeoJSON) sampleZonesGeoJSON.push(zonesGeoJSON);
				}

				// Collect GeoJSON from zone maps
				const fieldZones = zones
					.filter((event) => event.field_id === field.id)
					.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
				const zoneMapsGeoJSON: GeoJSON.FeatureCollection[] = [];
				for (const event of fieldZones) {
					const zoneGeoJSON = await Agrian.getZoneMapGeoJSON(event);
					if (zoneGeoJSON) zoneMapsGeoJSON.push(zoneGeoJSON);
				}

				// Sample Events are preferred over Target Events
				const allCentroidsGeoJSON = [...sampleCentroidsGeoJSON, ...targetCentroidsGeoJSON];
				const allCoresGeoJSON = [...sampleCoresGeoJSON];
				let pointsGeoJSON: GeoJSON.FeatureCollection | undefined;
				if (isZoneDeal) {
					pointsGeoJSON = allCoresGeoJSON.length > 0 ? allCoresGeoJSON[0] : undefined;
				} else if (sampleCentroidsGeoJSON.length > 0) {
					pointsGeoJSON = sampleCentroidsGeoJSON[0];
				} else if (targetCentroidsGeoJSON.length > 0) {
					pointsGeoJSON = targetCentroidsGeoJSON[0];
				}

				// Sample Events are preferred over Target Events and Zones
				const allZonesGeoJSON = isZoneDeal ? [...sampleZonesGeoJSON, ...targetZonesGeoJSON, ...zoneMapsGeoJSON] : [];
				const zonesGeoJSON = allZonesGeoJSON.length > 0 ? allZonesGeoJSON[0] : undefined;

				const boundaryOption: GeoJSONOption = {
					name: optionName(field.created_at, field, "boundary"),
					geoJSON: boundaryGeoJSON,
				};
				const centroidsOptions: GeoJSONOption[] = allCentroidsGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "centroids", i),
					geoJSON: gj,
				}));
				const coresOptions: GeoJSONOption[] = allCoresGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "cores", i),
					geoJSON: gj,
				}));
				const zonesOptions: GeoJSONOption[] = allZonesGeoJSON.map((gj, i) => ({
					name: optionName(field.created_at, field, "zones", i),
					geoJSON: gj,
				}));

				const newJob: Job = defaultJobForImport(
					field.name,
					deal.depth,
					deal.density,
					grower.name,
					deal.frequencyOfMicros,
					deal.testPackage,
					farm.name,
				);

				newJob.files = {
					pointsGeoJSON: pointsGeoJSON ? normalizePointsFC(pointsGeoJSON) : undefined,
					boundaryGeoJSON: boundaryGeoJSON,
					zonesGeoJSON: zonesGeoJSON,
				};

				newJob.fileOptions = {
					boundaryOptions: [{ type: FileOptionTypes["Boundary"], options: [boundaryOption] }],
					boundaryGeoJSON: boundaryGeoJSON,
					pointsOptions: [
						{ type: FileOptionTypes["Centroids"], options: centroidsOptions },
						{ type: FileOptionTypes["Cores"], options: coresOptions },
					],
					pointsGeoJSON: pointsGeoJSON,
					zonesOptions: [{ type: FileOptionTypes["Zones"], options: zonesOptions }],
					zonesGeoJSON: zonesGeoJSON,
				};
				newJob.dealIds = deal.id ? [deal.id] : [];
				newJob.labAccountNumber = getLabAccountNumber(deal, grower, _growerBranchMap);
				newJob.externalFarmId = farm.id;
				newJob.fieldId = field.id;
				newJob.fieldPriority = "Regular Turn (default)";
				newJob.submitterEmail = props.user?.email;
				newJob.submitterName = props.user?.name;
				newJob.branchIds = internalSelectedBranch ? [internalSelectedBranch.id] : [];
				newJob.externalGrower = {
					id: grower.id,
					name: grower.name,
				};
				newJob.eventId = ""; // this is intentionally blank. We do not want to pull in original event ID. Bnd Calc handles event ID.

				newJobs.push(newJob);
			}

			const stats = {
				zones: newJobs.reduce((sum, job) => sum + (job.files.zonesGeoJSON?.features?.length || 0), 0),
				centroids: newJobs.reduce(
					(sum, job) => sum + (isZoneDeal ? 0 : job.files.pointsGeoJSON?.features?.length || 0),
					0,
				),
				cores: newJobs.reduce((sum, job) => sum + (isZoneDeal ? job.files.pointsGeoJSON?.features?.length || 0 : 0), 0),
			};
			setFeatureStats(stats);

			setJobs(newJobs);

			// If in modal mode, notify parent and let it handle the ImportTable
			if (props.onJobsExtracted) {
				props.onJobsExtracted(newJobs, stats);
			}
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.AgrianImportForm.getSamplingEvents");
		} finally {
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
		}
	}, [
		props,
		selectedGrowers,
		selectedFields,
		farms,
		selectedLabPackages.length,
		internalSelectedDeal,
		internalSelectedBranch,
	]);

	return {
		// State
		organizations,
		selectedOrganizations,
		growers,
		selectedGrowers,
		farms,
		fields,
		selectedFields,
		jobs,
		featureStats,
		loading,

		// Internal Branch/Deal selection
		internalSelectedBranch,
		internalSelectedDeal,
		canShowBranchSelector,
		handleInternalBranchChange,
		handleInternalDealChange,

		// Computed values
		canLoadOrganizations,
		showOrganizationSelector,
		canLoadGrowers,
		showGrowerSelector,
		canLoadFarmsAndFields,
		showFieldSelector,
		canLoadJobs,
		showLoadingSpinner,
		showImportTable,

		// Context
		userContext,

		// State setters
		setSelectedOrganizations,
		setSelectedGrowers,
		setSelectedFields,

		// Event handlers
		handleOrganizationChange,
		handleGrowerChange,
		handleFieldChange,

		// API calls
		getOrganizations,
		getGrowers,
		getFarmsAndFields,
		getSamplingEvents,

		// Reset
		resetForm,
	};
}
